import React from "react";
import { useTheme } from "@mui/material/styles";

/** Mindmapper AI – Flow Graph (final) */
const NODE_W = 240;
const NODE_H = 90;
const GAP_V  = 20;
const GAP_H  = 50;

const Node = ({ x, y, label, stroke, fill, fontFamily, fontColor }) => (
  <g filter="url(#shadow)">
    <rect
      x={x}
      y={y}
      width={NODE_W}
      height={NODE_H}
      rx="12"
      ry="12"
      fill={fill}
      stroke={stroke}
      strokeWidth={2}
    />
    <foreignObject x={x} y={y} width={NODE_W} height={NODE_H}>
      <div
        style={{
          height: "100%",
          width:  "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 8,
          fontSize: 13,
          fontWeight: 600,
          fontFamily,
          color: fontColor,
          textAlign: "center",
          whiteSpace: "pre-wrap",
          lineHeight: 1.35,
        }}
      >
        {label}
      </div>
    </foreignObject>
  </g>
);

export default function MindmapperGraph() {
  const theme      = useTheme();
  const primary    = theme.palette.primary.main;
  const textColor  = theme.palette.text.primary;
  const paper      = theme.palette.background.paper;
  const shadowCol  = "rgba(0,0,0,0.15)";

  /* ---------- layout helpers ---------- */
  const bubble   = { cx: 450, cy: 300, r: 110 };

  const xLeft    = bubble.cx - bubble.r - GAP_H - NODE_W; // 70
  const xRight   = bubble.cx + bubble.r + GAP_H;          // 610
  const xCenter  = bubble.cx - NODE_W / 2;                // 330

  const y = {
    // left + right stacks
    l1 : 30,
    l2 : 30 + NODE_H + GAP_V,
    l3 : 30 + 2 * (NODE_H + GAP_V),   // 250
    r1 : 30,
    r2 : 30 + NODE_H + GAP_V,
    r3 : 30 + 2 * (NODE_H + GAP_V),
    // centre column under bubble
    inner     : bubble.cy + bubble.r + GAP_V,                          // 430
    crafting  : bubble.cy + bubble.r + GAP_V + NODE_H + GAP_V,         // 540
    wizard    : bubble.cy + bubble.r + GAP_V + 2 * (NODE_H + GAP_V),   // 650
  };

  return (
    <div
      style={{
        display: "flex", // Flexbox for centering
        justifyContent: "center", // Horizontal centering
        alignItems: "center", // Vertical centering
        overflowX: "auto",
        padding: 24,
        background: theme.palette.background.default,
      }}
    >
      <svg viewBox="0 0 900 900" style={{ width: 900, maxWidth: "100%" }} preserveAspectRatio="xMidYMin meet">
        {/* ---------- defs ---------- */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor={shadowCol} />
          </filter>
          <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="10" result="blur" />
            <feFlood floodColor={primary} result="glowColor" />
            <feComposite in="glowColor" in2="blur" operator="in" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <radialGradient id="centreGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#F7F6F4" />
          </radialGradient>
        </defs>

        {/* ---------- animations ---------- */}
        <style>{`
          .flow{ stroke-dasharray:8 6; animation:dash 5s linear infinite; }
          @keyframes dash{ to{ stroke-dashoffset:-140; } }
          .pulse{ animation:pulse 3s ease-in-out infinite; transform-origin:${bubble.cx}px ${bubble.cy}px; }
          @keyframes pulse{
            0%,100%{ transform:scale(1); }
            50%    { transform:scale(1.06); }
          }
        `}</style>

        {/* ---------- nodes ---------- */}
        {/* left column */}
        <Node 
          x={xLeft} 
          y={y.l1} 
          label={`Log your\nDream Daily`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xLeft} 
          y={y.l2} 
          label={`AI‑Based\nInterpretation`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xLeft} 
          y={y.l3} 
          label={`Understand your emotions,\nfeelings & state of mind`} 
          stroke={primary} 
          fill={primary}  // <-- changed to primary color
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />

        {/* right column */}
        <Node 
          x={xRight} 
          y={y.r1} 
          label={`Revisit your\nDreams Later`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xRight} 
          y={y.r2} 
          label={`Find connections & patterns\nusing AI`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xRight} 
          y={y.r3} 
          label={`Understand your flaws,\nstrengths, fears & desires`} 
          stroke={primary} 
          fill={primary}  // <-- changed to primary color
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />

        {/* central + lower stack */}
        <Node 
          x={xCenter} 
          y={y.inner}    
          label={`Understand your\nInner Self`} 
          stroke={primary} 
          fill={primary}  // <-- changed to primary color
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xCenter} 
          y={y.crafting} 
          label={`AI Crafting\nPerfect Meditation\nfor You`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xCenter} 
          y={y.wizard}   
          label={`Meditation Wizard\nwith full customization`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />

        <Node 
          x={xLeft}  
          y={y.crafting} 
          label={`Your Custom\nInstructions`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />
        <Node 
          x={xRight} 
          y={y.crafting} 
          label={`Personalized for your\nMind using Dreams`} 
          stroke={primary} 
          fill={paper} 
          fontFamily={theme.typography.fontFamily} 
          fontColor={textColor}
        />

        
        {/* ---------- centre bubble ---------- */}
          <g className="pulse">
            <circle 
              cx={bubble.cx} 
              cy={bubble.cy} 
              r={bubble.r} 
              fill="#292E34"  // <-- dark blue background
              stroke={primary} 
              strokeWidth={3} 
              filter="url(#glow)"  // <-- glow filter using primary color
            />
            <text
              x={bubble.cx}
              y={bubble.cy}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={24}
              fontWeight={700}
              fontFamily={theme.typography.fontFamily}
              fill="#FFFFFF"  // <-- changed to white color
            >
              Mindmapper AI
            </text>
          </g>

          {/* ---------- flow lines ---------- */}
        {/* vertical ladders in columns */}
        <path d={`M${xLeft  + NODE_W/2} ${y.l1+NODE_H} V ${y.l2}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>
        <path d={`M${xLeft  + NODE_W/2} ${y.l2+NODE_H} V ${y.l3}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>
        <path d={`M${xRight + NODE_W/2} ${y.r1+NODE_H} V ${y.r2}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>
        <path d={`M${xRight + NODE_W/2} ${y.r2+NODE_H} V ${y.r3}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>

        {/* bubble → spine */}
        <path d={`M${bubble.cx} ${bubble.cy + bubble.r} V ${y.inner}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>
        <path d={`M${bubble.cx} ${y.inner + NODE_H} V ${y.crafting}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>
        <path d={`M${bubble.cx} ${y.crafting + NODE_H} V ${y.wizard}`} className="flow" stroke={primary} strokeWidth={2} fill="none"/>

        {/* side curves into bubble */}
        <path
          d={`
            M${xLeft + NODE_W} ${y.l3 + NODE_H/2}
            C ${xLeft + NODE_W + 60} ${y.l3 + NODE_H/2 + 20},
              ${bubble.cx - bubble.r - 20} ${bubble.cy - 10},
              ${bubble.cx - bubble.r} ${bubble.cy}
          `}
          className="flow"
          stroke={primary}
          strokeWidth={2}
          fill="none"
        />
        <path
          d={`
            M${xRight} ${y.r3 + NODE_H/2}
            C ${xRight - 60} ${y.r3 + NODE_H/2 + 20},
              ${bubble.cx + bubble.r + 20} ${bubble.cy - 10},
              ${bubble.cx + bubble.r} ${bubble.cy}
          `}
          className="flow"
          stroke={primary}
          strokeWidth={2}
          fill="none"
        />

        {/* side curves into AI Crafting */}
        <path
          d={`
            M${xLeft + NODE_W} ${y.crafting + NODE_H/2}
            C ${xLeft + NODE_W + 40} ${y.crafting + NODE_H/2},
              ${bubble.cx - NODE_W/2 - 40} ${y.crafting + NODE_H/2},
              ${bubble.cx - NODE_W/2} ${y.crafting + NODE_H/2}
          `}
          className="flow"
          stroke={primary}
          strokeWidth={2}
          fill="none"
        />
        <path
          d={`
            M${xRight} ${y.crafting + NODE_H/2}
            C ${xRight - 40} ${y.crafting + NODE_H/2},
              ${bubble.cx + NODE_W/2 + 40} ${y.crafting + NODE_H/2},
              ${bubble.cx + NODE_W/2} ${y.crafting + NODE_H/2}
          `}
          className="flow"
          stroke={primary}
          strokeWidth={2}
          fill="none"
        />
      </svg>
    </div>
  );
}
